import { MediaUploadUsage, type MediaUploadUsageValue } from '@/lib/enums/MediaUploadUsage'

const MB = 1024 * 1024
const KB = 1024

export const DEFAULT_UPLOAD_USAGE = MediaUploadUsage.ImageStandard.Value

/** The longest side the server keeps for an order photo (`MediaWebpEncoder::ORDER_PHOTO_MAX_DIMENSION`). */
export const ORDER_PHOTO_MAX_DIMENSION = 2560

/** What a file needs to be uploaded for a usage. */
export interface MediaUploadProfile {
  usage: MediaUploadUsageValue
  note: string
  /** Uppy's restrictions; file sizes are in bytes and file types are extensions. */
  restrictions: {
    minFileSize: number
    maxFileSize: number
    minNumberOfFiles: number
    maxNumberOfFiles: number
    allowedFileTypes: string[]
  }
  /** The largest saved photo, in pixels, that can be chosen for this usage. */
  savedMediaRestrictions?: {
    maxWidth: number
    maxHeight: number
    maxPixels: number
  }
}

export const mediaUploadProfiles: Record<MediaUploadUsageValue, MediaUploadProfile> = {
  [MediaUploadUsage.OrderPhoto.Value]: {
    usage: MediaUploadUsage.OrderPhoto.Value,
    note: 'Images up to 50 MB.',
    restrictions: {
      minFileSize: 1 * KB,
      maxFileSize: 50 * MB,
      minNumberOfFiles: 1,
      maxNumberOfFiles: 1,
      allowedFileTypes: ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.avif', '.heic', '.heif'],
    },
  },
  [MediaUploadUsage.ImageStandard.Value]: {
    usage: MediaUploadUsage.ImageStandard.Value,
    note: 'PNG, JPG, GIF, WEBP, AVIF, HEIC, or HEIF files up to 5MB',
    restrictions: {
      minFileSize: 1 * KB,
      maxFileSize: 5 * MB,
      minNumberOfFiles: 1,
      maxNumberOfFiles: 1,
      allowedFileTypes: ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.avif', '.heic', '.heif'],
    },
  },
  [MediaUploadUsage.StorefrontHero.Value]: {
    usage: MediaUploadUsage.StorefrontHero.Value,
    note: 'For best results, use a 3840 × 2160 WEBP under 1MB. Maximum 5MB.',
    restrictions: {
      minFileSize: 1 * KB,
      maxFileSize: 5 * MB,
      minNumberOfFiles: 1,
      maxNumberOfFiles: 1,
      allowedFileTypes: ['.png', '.jpg', '.jpeg', '.webp', '.avif', '.heic', '.heif'],
    },
    savedMediaRestrictions: {
      maxWidth: 3840,
      maxHeight: 3840,
      maxPixels: 8294400,
    },
  },
  [MediaUploadUsage.FaviconIcon.Value]: {
    usage: MediaUploadUsage.FaviconIcon.Value,
    note: 'ICO or PNG files up to 512KB',
    restrictions: {
      minFileSize: 2,
      maxFileSize: 512 * KB,
      minNumberOfFiles: 1,
      maxNumberOfFiles: 1,
      allowedFileTypes: ['.ico', '.png'],
    },
  },
}

export const getMediaUploadProfile = (
  usage: MediaUploadUsageValue = DEFAULT_UPLOAD_USAGE
): MediaUploadProfile => {
  // Untyped callers can pass a usage that has no profile.
  return mediaUploadProfiles[usage] ?? mediaUploadProfiles[DEFAULT_UPLOAD_USAGE]
}
